import React, { memo } from "react";
import t from "../../../../../../../i18n";
import cx from "classnames";
import { GroupLine, LockLine, UserLine } from "@ui/IconV2/react";
import { colors, spacings } from "@ui/Theme";
import { printableHidden } from "../../../../utils/print";
import { css } from "@linaria/core";
import { Tag } from "@ui/Tag";
import { Text } from "@ui/Text";
import TooltipV2 from "@ui/TooltipV2";
import { DashboardUsage } from "./DashboardUsage";
import { useTranslations } from "../../../../../../../api/useTranslations";

export const Status = memo(
  ({
    projectId,
    dashboardId,
    published,
    readonly,
    username,
    className,
  }: {
    projectId: string;
    dashboardId: string;
    published?: boolean | null;
    readonly?: boolean | null;
    username?: string | null;
    className?: string;
  }) => {
    const translations = useTranslations();

    return (
      <div className={cx(status, printableHidden, className)}>
        <Text type="body1" className={publicationStatus}>
          {published ? <GroupLine /> : <UserLine />}{" "}
          {published
            ? t("project.dashboard.status.published")
            : t("project.dashboard.status.unpublished")}
        </Text>
        <span className={statusSeparator}>・</span>
        <div className={statusSection}>
          <Text type="body1">
            <span className={statusLabel}>{t("project.dashboard.author.prefix")}</span>{" "}
            {username || t("project.dashboard.author.anonymous")}
          </Text>
          {published && readonly && (
            <TooltipV2
              overlay={t(
                "project.dashboard.transaction.manage_sharing.access.hint.readonly",
                translations,
              )}
            >
              <div>
                <Tag className={printableHidden}>
                  <LockLine size="xs" />
                  <span>{t("project.dashboard.status.readonly")}</span>
                </Tag>
              </div>
            </TooltipV2>
          )}
        </div>
        <span className={statusSeparator}>・</span>
        <DashboardUsage
          projectId={projectId}
          dashboardId={dashboardId}
          isPrivate={published === false}
        />
      </div>
    );
  },
);

//#region CSS
const status = css`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  row-gap: ${spacings.xxs};
`;
const statusSection = css`
  align-items: inherit;
  display: inherit;
  flex-wrap: inherit;
  gap: ${spacings.xs};

  p {
    white-space: nowrap;
  }
`;
const publicationStatus = css`
  align-items: center;
  display: inline-flex;
  gap: ${spacings.xxs};
  color: ${colors.carbon900};
`;
const statusLabel = css`
  color: ${colors.carbon700};
`;
const statusSeparator = css`
  color: ${colors.carbon800};
  margin: 0 2px;

  &:not(:has(+ *)) {
    display: none;
  }
`;
//#endregion CSS
